import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RejectWithMessage, TableDataResponse } from 'features/types';
import { getErrorMessage } from 'utils/error';
import { RootState } from 'app/root-reducer';
import { Paging } from 'api/types';
import { GetJobRespondsByOrganizationTableFilters, JobRespondsByOrganizationTable, JobResponsesByOrganizationState } from './types';
import { jobResponsesAPI } from 'api';

export const getTable = createAsyncThunk<TableDataResponse<JobRespondsByOrganizationTable>, void, RejectWithMessage>(
  'job-responses-by-organization/get-table',
  async(_, { rejectWithValue, getState }) => {
    try {
      const state: RootState = getState();
      const requestData = state.jobResponsesByOrganization.requestData;
      return jobResponsesAPI.getByOrganizationTable(requestData)
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

const initialState: JobResponsesByOrganizationState = {
  status: 'idle',
  error: null,
  requestData: {
    paging: {
      page: 1,
      pageSize: 20,
    },
    filters: {},
  },
  data: {
    tableDataIsLoading: false,
    tableData: {
      rows: [],
      totalCount: 0,
      noResult: false,
    },
  }
};

const jobResponsesByOrganizationSlice = createSlice({
  name: 'job-responses-by-organization',
  initialState,
  reducers: {
    setPage: (state, action: PayloadAction<Paging>) => {
      state.requestData.paging.page = action.payload.page;
      state.requestData.paging.pageSize = action.payload.pageSize;
    },
    resetState: () => initialState,
    setFilters: (state, action: PayloadAction<GetJobRespondsByOrganizationTableFilters>) => {
      state.requestData.filters = { ...action.payload };
      state.requestData.paging = initialState.requestData.paging;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getTable.pending, (state) => {
      state.data.tableDataIsLoading = true;
    });
    builder.addCase(getTable.fulfilled, (state, action: PayloadAction<TableDataResponse<JobRespondsByOrganizationTable>>) => {
      state.data.tableDataIsLoading = false;
      state.data.tableData = action.payload;
      state.data.tableData.noResult = state.data.tableData.rows.length === 0;
    });
    builder.addCase(getTable.rejected, (state) => {
      state.data.tableDataIsLoading = false;
    });
  }
});

export const { setPage, resetState, setFilters } = jobResponsesByOrganizationSlice.actions;

export const jobResponsesByOrganizationReducer = jobResponsesByOrganizationSlice.reducer;
